import { useMemo } from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { DraftAccount } from "./draftOperations";
import { GetRowCapabilities } from "./editorCapabilities";
import { ChartOfAccountsTemplateEditorRow } from "./editorRowTypes";
import {
    filterDeletableAccountIds,
    filterDisablableAccountIds,
    filterEnablableAccountIds,
} from "./accountSelectionFilters";

export interface EditorSelectionGates {
    // Selected account ids restricted to currently-visible bulk-actionable rows
    // (placeholder == null). Rows selected then hidden by the search filter stay
    // in `rowSelection` but are intersected out here.
    selectedVisibleAccountIds: string[];
    // Count of rows currently visible (every row is checkbox-selectable).
    selectableCount: number;
    // Count of ALL checked rows currently visible, INCLUDING placeholders —
    // drives the footer's "N selected" total.
    selectedVisibleCount: number;
    // The selected accounts that may actually be bulk-deleted / bulk-archived.
    deletableSelectedAccountIds: string[];
    disablableSelectedAccountIds: string[];
    // True when a bulk-actionable, enabled account in the selection has its
    // Archive blocked by a host capability.
    isBulkArchiveBlocked: boolean;
    canBulkDelete: boolean;
    canBulkDisable: boolean;
    canBulkEnable: boolean;
    // The disabled Archive / Delete tooltip strings, derived from the host
    // capabilities of the bulk-actionable selection (undefined when no blocked
    // reason exists).
    bulkArchiveBlockReason: string | undefined;
    bulkDeleteBlockReason: string | undefined;
}

interface UseEditorSelectionGatesParams {
    accounts: DraftAccount[];
    visibleRows: ChartOfAccountsTemplateEditorRow[];
    rowSelection: RowSelectionState;
    getRowCapabilities: GetRowCapabilities;
    // Needed because in the merge view display-key `rowSelection` does not
    // intersect `visibleRows`.
    selectedAccountIds?: string[];
}

// The body-local selection + bulk-action gates (and the bulk tooltip strings).
// Reads visibleRows + rowSelection (body state) and the host's getRowCapabilities.
export const useEditorSelectionGates = ({
    accounts,
    visibleRows,
    rowSelection,
    getRowCapabilities,
    selectedAccountIds,
}: UseEditorSelectionGatesParams): EditorSelectionGates => {
    const actionableVisibleRows = useMemo(
        () =>
            visibleRows.filter(
                (row) => getRowCapabilities(row.account).placeholder == null,
            ),
        [visibleRows, getRowCapabilities],
    );

    const selectedVisibleAccountIds = useMemo(() => {
        if (selectedAccountIds !== undefined) {
            const accountsById = new Map(
                accounts.map((account) => [account.id, account]),
            );
            return selectedAccountIds.filter((id) => {
                const account = accountsById.get(id);
                return (
                    account != null &&
                    getRowCapabilities(account).placeholder == null
                );
            });
        }
        const actionableVisibleIds = new Set(
            actionableVisibleRows.map((row) => row.id),
        );
        return Object.entries(rowSelection)
            .filter(
                ([id, isSelected]) =>
                    isSelected && actionableVisibleIds.has(id),
            )
            .map(([id]) => id);
    }, [
        accounts,
        selectedAccountIds,
        getRowCapabilities,
        rowSelection,
        actionableVisibleRows,
    ]);

    const selectableCount = visibleRows.length;

    const selectedVisibleCount = useMemo(() => {
        const visibleIds = new Set(visibleRows.map((row) => row.id));
        return Object.entries(rowSelection).filter(
            ([id, isSelected]) => isSelected && visibleIds.has(id),
        ).length;
    }, [rowSelection, visibleRows]);

    const deletableSelectedAccountIds = useMemo(
        () =>
            filterDeletableAccountIds({
                accounts,
                selectedIds: selectedVisibleAccountIds,
                getRowCapabilities,
            }),
        [accounts, selectedVisibleAccountIds, getRowCapabilities],
    );

    const disablableSelectedAccountIds = useMemo(
        () =>
            filterDisablableAccountIds({
                accounts,
                selectedIds: selectedVisibleAccountIds,
                getRowCapabilities,
            }),
        [accounts, selectedVisibleAccountIds, getRowCapabilities],
    );

    const selectedActionableAccounts = useMemo(() => {
        const selectedIds = new Set(selectedVisibleAccountIds);
        return accounts.filter((account) => selectedIds.has(account.id));
    }, [accounts, selectedVisibleAccountIds]);

    const isBulkArchiveBlocked = useMemo(
        () =>
            selectedActionableAccounts.some(
                (account) =>
                    !account.isDisabled &&
                    getRowCapabilities(account).archiveBlockReason != null,
            ),
        [selectedActionableAccounts, getRowCapabilities],
    );

    const canBulkDelete =
        selectedVisibleAccountIds.length > 0 &&
        deletableSelectedAccountIds.length === selectedVisibleAccountIds.length;

    const canBulkDisable =
        disablableSelectedAccountIds.length > 0 && !isBulkArchiveBlocked;

    const canBulkEnable = useMemo(
        () =>
            filterEnablableAccountIds({
                accounts,
                selectedIds: selectedVisibleAccountIds,
            }).length > 0,
        [accounts, selectedVisibleAccountIds],
    );

    // Archive tooltip: the first bulk-actionable enabled account whose Archive
    // is blocked by a host capability.
    const bulkArchiveBlockReason = useMemo(
        () =>
            selectedActionableAccounts
                .filter((account) => !account.isDisabled)
                .map(
                    (account) => getRowCapabilities(account).archiveBlockReason,
                )
                .find((reason) => reason != null) ?? undefined,
        [selectedActionableAccounts, getRowCapabilities],
    );

    // Delete tooltip: the first bulk-actionable account whose Delete is blocked.
    const bulkDeleteBlockReason = useMemo(
        () =>
            selectedActionableAccounts
                .map((account) => getRowCapabilities(account).deleteBlockReason)
                .find((reason) => reason != null) ?? undefined,
        [selectedActionableAccounts, getRowCapabilities],
    );

    return {
        selectedVisibleAccountIds,
        selectableCount,
        selectedVisibleCount,
        deletableSelectedAccountIds,
        disablableSelectedAccountIds,
        isBulkArchiveBlocked,
        canBulkDelete,
        canBulkDisable,
        canBulkEnable,
        bulkArchiveBlockReason,
        bulkDeleteBlockReason,
    };
};
